import klaxMascotVideoLight from 'assets/images/magnific_mascott-t-light.mp4';
import klaxMascotVideoDark from 'assets/images/magnific_mascott-t.mp4';
import { useTheme } from 'components/core/theme-provider';
import { MOBILE_BREAKPOINT } from 'hooks/use-mobile';
import { cn } from 'lib/utils';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/** Figma 2932:27981 — mascot size */
const MASCOT_SIZE = 50;
/** Figma 1327:11351 — compact row gap */
const COMPACT_GAP_PX = 10;
/** Figma 2932:27980 — stacked gap between buddy and text */
const STACKED_GAP_CLASS = 'gap-[18px]';
/** Figma 2932:27983 — stacked text width */
const STACKED_TEXT_MAX_WIDTH_PX = 600;
const STACKED_TEXT_WIDTH_CLASS = 'max-w-[600px]';

export interface ChatGreetingProps {
  className?: string;
  title: string;
}

function ChatGreetingMascot() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const klaxMascotVideo = isDark ? klaxMascotVideoDark : klaxMascotVideoLight;
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = 0;

    void video.play().catch(() => {
      // Autoplay may be blocked; keep the first frame visible.
    });
  }, [klaxMascotVideo]);

  const handleVideoEnded = () => {
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = 0;
    video.pause();
  };

  return (
    <div
      className="size-[50px] shrink-0 overflow-hidden rounded-full"
      style={{ width: MASCOT_SIZE, height: MASCOT_SIZE }}
    >
      <video
        ref={videoRef}
        key={resolvedTheme}
        src={klaxMascotVideo}
        muted
        playsInline
        preload="auto"
        aria-hidden
        onEnded={handleVideoEnded}
        className={cn(
          'size-full object-cover',
          isDark ? 'scale-[1.30] mix-blend-screen' : 'scale-[1.30]'
        )}
        width={MASCOT_SIZE}
        height={MASCOT_SIZE}
      />
    </div>
  );
}

export function ChatGreeting({ className, title }: ChatGreetingProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [isStackedLayout, setIsStackedLayout] = useState(false);

  const titleClassName = cn(
    'font-sans text-xl sm:text-3xl 2xl:text-[40px] font-normal leading-none',
    isDark ? 'text-[#999781]' : 'text-foreground'
  );

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateLayout = () => {
      const measure = measureRef.current;
      if (!measure) return;

      const containerWidth = container.clientWidth;
      if (containerWidth === 0) return;

      const textWidth = measure.offsetWidth;
      const availableCompactWidth = containerWidth - MASCOT_SIZE - COMPACT_GAP_PX;

      // Mobile: always stack mascot above text (same as loading-overlay).
      setIsStackedLayout(
        containerWidth < MOBILE_BREAKPOINT ||
          textWidth > STACKED_TEXT_MAX_WIDTH_PX ||
          textWidth > availableCompactWidth
      );
    };

    updateLayout();

    const resizeObserver = new ResizeObserver(updateLayout);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [title]);

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative flex w-full',
        isStackedLayout
          ? cn('flex-col items-center', STACKED_GAP_CLASS)
          : 'items-center justify-center gap-2.5',
        className
      )}
    >

      {/* <ChatGreetingMascot /> */}


      <p
        className={cn(
          titleClassName,
          'text-center',
          isStackedLayout
            ? cn('w-full min-w-0 break-words', STACKED_TEXT_WIDTH_CLASS)
            : 'whitespace-nowrap'
        )}
      >
        {title}
      </p>
    </div>
  );
}
